/**
 * Service Custom Field Service
 *
 * Manages custom field definitions and org defaults for internal services.
 */

import ApiService from './api.service';

// Valid field types
export const FIELD_TYPES = [
  { value: 'TEXT', label: 'Text' },
  { value: 'TEXTAREA', label: 'Text Area' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'DATE', label: 'Date' },
  { value: 'SELECT', label: 'Dropdown' }
];

// UAE default custom fields per service template key
export const UAE_DEFAULT_CUSTOM_FIELDS = {
  'UAE-MEDICAL': [
    { code: 'MEDICAL_CENTER', label: 'Medical Center', fieldType: 'SELECT', required: false, options: [{ value: 'DHA Dubai', label: 'DHA Dubai' }, { value: 'SEHA Abu Dhabi', label: 'SEHA Abu Dhabi' }, { value: 'EHS', label: 'EHS' }] },
    { code: 'TEST_TYPE', label: 'Test Type', fieldType: 'SELECT', required: false, options: [{ value: 'Pre-employment', label: 'Pre-employment' }, { value: 'Renewal', label: 'Renewal' }, { value: 'Fitness', label: 'Fitness' }] },
    { code: 'APPOINTMENT_DATE', label: 'Appointment Date', fieldType: 'DATE', required: false }
  ],
  'UAE-EJARI': [
    { code: 'PROPERTY_ADDRESS', label: 'Property Address', fieldType: 'TEXT', required: false },
    { code: 'RENTAL_AMOUNT', label: 'Rental Amount (Annual)', fieldType: 'NUMBER', required: false },
    { code: 'LANDLORD_NAME', label: 'Landlord Name', fieldType: 'TEXT', required: false },
    { code: 'LEASE_START', label: 'Lease Start Date', fieldType: 'DATE', required: false },
    { code: 'LEASE_END', label: 'Lease End Date', fieldType: 'DATE', required: false }
  ],
  'UAE-VISA-NEW': [
    { code: 'JOB_TITLE', label: 'Job Title', fieldType: 'TEXT', required: false },
    { code: 'SALARY', label: 'Salary (AED)', fieldType: 'NUMBER', required: false },
    { code: 'VISA_CATEGORY', label: 'Visa Category', fieldType: 'SELECT', required: false, options: [{ value: 'Professional', label: 'Professional' }, { value: 'Skilled', label: 'Skilled' }, { value: 'Labour', label: 'Labour' }] }
  ],
  'UAE-VISA-RENEW': [
    { code: 'CURRENT_VISA_NO', label: 'Current Visa Number', fieldType: 'TEXT', required: false },
    { code: 'VISA_EXPIRY', label: 'Visa Expiry Date', fieldType: 'DATE', required: false }
  ],
  'UAE-VISA-CANCEL': [
    { code: 'CANCEL_REASON', label: 'Cancellation Reason', fieldType: 'SELECT', required: false, options: [{ value: 'Resignation', label: 'Resignation' }, { value: 'Termination', label: 'Termination' }, { value: 'Transfer', label: 'Transfer' }] }
  ],
  'UAE-EID-NEW': [
    { code: 'DELIVERY_ADDRESS', label: 'Delivery Address', fieldType: 'TEXT', required: false },
    { code: 'BIOMETRIC_LOC', label: 'Biometric Location', fieldType: 'SELECT', required: false, options: [{ value: 'AMER Center 1', label: 'AMER Center 1' }, { value: 'AMER Center 2', label: 'AMER Center 2' }] }
  ],
  'UAE-LABOUR-NEW': [
    { code: 'OCCUPATION_CODE', label: 'Occupation Code', fieldType: 'TEXT', required: false }
  ],
  'UAE-TRADE-NEW': [
    { code: 'BUSINESS_ACTIVITY', label: 'Business Activity', fieldType: 'TEXT', required: false },
    { code: 'LICENSED_PREMISES', label: 'Licensed Premises', fieldType: 'TEXT', required: false }
  ],
  'UAE-TRANSLATION': [
    { code: 'SOURCE_LANG', label: 'Source Language', fieldType: 'SELECT', required: false, options: [{ value: 'Arabic', label: 'Arabic' }, { value: 'English', label: 'English' }, { value: 'Hindi', label: 'Hindi' }, { value: 'Urdu', label: 'Urdu' }] },
    { code: 'TARGET_LANG', label: 'Target Language', fieldType: 'SELECT', required: false, options: [{ value: 'Arabic', label: 'Arabic' }, { value: 'English', label: 'English' }, { value: 'Hindi', label: 'Hindi' }, { value: 'Urdu', label: 'Urdu' }] },
    { code: 'PAGE_COUNT', label: 'Page Count', fieldType: 'NUMBER', required: false },
    { code: 'CERTIFIED', label: 'Certified', fieldType: 'SELECT', required: false, options: [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }] }
  ],
  'UAE-INSURANCE': [
    { code: 'INSURANCE_TYPE', label: 'Insurance Type', fieldType: 'SELECT', required: false, options: [{ value: 'Health', label: 'Health' }, { value: 'Worker', label: 'Worker' }, { value: 'Professional', label: 'Professional' }] },
    { code: 'COVERAGE_AMOUNT', label: 'Coverage Amount', fieldType: 'NUMBER', required: false }
  ],
  'UAE-WPS': [
    { code: 'NUM_EMPLOYEES', label: 'Number of Employees', fieldType: 'NUMBER', required: false }
  ]
};

const getTypingCenterId = () => (
  localStorage.getItem('typingCenterId') ||
  JSON.parse(localStorage.getItem('currentUser') || '{}')?.typingCenterId
);

const serviceFieldService = {
  // ===== TC-level catalog (reusable fields) =====
  getCatalog: async (includeArchived = false) => {
    const typingCenterId = getTypingCenterId();
    const response = await ApiService.get('/api/tc-custom-fields', {
      params: { typingCenterId, includeArchived }
    });
    return response?.data?.data || [];
  },

  createCatalogField: async (data) => {
    const typingCenterId = getTypingCenterId();
    const response = await ApiService.post('/api/tc-custom-fields', data, {
      params: { typingCenterId }
    });
    return response?.data?.data || response?.data;
  },

  updateCatalogField: async (fieldId, data) => {
    const response = await ApiService.put(`/api/tc-custom-fields/${fieldId}`, data);
    return response?.data?.data || response?.data;
  },

  archiveCatalogField: async (fieldId) => {
    await ApiService.delete(`/api/tc-custom-fields/${fieldId}`);
  },

  /**
   * Get custom fields for a service (resolved from the catalog via the service links)
   */
  getFields: async (serviceId) => {
    const response = await ApiService.get(`/api/internal-services/${serviceId}/custom-fields`);
    return response?.data?.data || [];
  },

  /**
   * Save custom fields for a service (replace all)
   */
  saveFields: async (serviceId, fields) => {
    const response = await ApiService.put(`/api/internal-services/${serviceId}/custom-fields`, { fields });
    return response?.data?.data || [];
  },

  /**
   * Get org defaults for a service's custom fields
   */
  getDefaults: async (serviceId, orgId) => {
    const response = await ApiService.get(`/api/internal-services/${serviceId}/field-defaults`, {
      params: { orgId }
    });
    return response?.data?.data || [];
  },

  /**
   * Save org defaults for a service's custom fields
   */
  saveDefaults: async (serviceId, orgId, defaults) => {
    await ApiService.put(`/api/internal-services/${serviceId}/field-defaults`, { orgId, defaults });
  }
};

export default serviceFieldService;
